import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

const navItems = (lang) => [
  {
    label: lang === "it" ? "Servizi" : "Leistungen",
    href: "/leistungen",
    dropdown: [
      { label: lang === "it" ? "Sopraelevazioni & Ampliamenti" : "Aufstockungen & Anbauten", href: "/leistungen/aufstockungen-anbauten" },
      { label: lang === "it" ? "Capriate & Strutture in Legno" : "Dachstühle & Holztragwerke", href: "/leistungen/dachstuehle-holztragwerke" },
      { label: lang === "it" ? "Case in Legno" : "Holzhäuser", href: "/leistungen/holzhaeuser" },
      { label: lang === "it" ? "Edilizia Alberghiera" : "Hotelbau & Tourismusbau", href: "/leistungen/hotelbau-tourismusbau" },
      { label: lang === "it" ? "Facciate in Legno" : "Holzfassaden", href: "/leistungen/holzfassaden" },
      { label: lang === "it" ? "Risanamenti & Restauri" : "Sanierungen & Restaurierungen", href: "/leistungen/sanierungen-restaurierungen" },
      { label: lang === "it" ? "Capannoni Industriali" : "Hallen- & Gewerbebauten", href: "/leistungen/hallen-gewerbebauten" },
      { label: lang === "it" ? "Terrazze & Balconi" : "Terrassen & Balkone", href: "/leistungen/terrassen-balkone" },
    ]
  },
  { label: lang === "it" ? "Progetti" : "Projekte", href: "/projekte" },
  { label: lang === "it" ? "Chi siamo" : "Über uns", href: "/ueber-uns" },
  { label: lang === "it" ? "Rivista" : "Magazin", href: "/magazin" },
  { label: lang === "it" ? "Lavoro" : "Karriere", href: "/karriere" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const { lang, setLang } = useLanguage();
  const items = navItems(lang);

  useEffect(() => { setMobileOpen(false); setDropdownOpen(false); }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-hb-border shadow-[0_1px_0_rgba(0,0,0,0.06)]"
    >
      <div className="container-hb flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex-shrink-0">
          <img
            src="/logo-blue-yellow.png"
            alt="Holzbuddy.team"
            className="h-8 md:h-9"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {items.map((item) => (
            item.dropdown ? (
              <div key={item.label} ref={dropdownRef} className="relative">
                <button
                  data-testid={`nav-${item.label.toLowerCase()}-btn`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1 text-xs tracking-widest uppercase font-semibold font-inter transition-colors duration-200 ${
                    isActive(item.href) ? "text-hb-yellow" : "text-hb-dark hover:text-hb-yellow"
                  }`}
                >
                  {item.label}
                  <ChevronDown size={12} className={`transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {dropdownOpen && (
                  <div
                    data-testid="leistungen-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-72 bg-white border border-hb-border shadow-xl py-2 z-50"
                  >
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        data-testid={`dropdown-${sub.href.split("/").pop()}`}
                        className="block px-5 py-3 text-xs text-hb-text hover:text-hb-dark hover:bg-hb-off-white transition-colors font-inter tracking-wide"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                className={`text-xs tracking-widest uppercase font-semibold font-inter transition-colors duration-200 ${
                  isActive(item.href) ? "text-hb-yellow" : "text-hb-dark hover:text-hb-yellow"
                }`}
              >
                {item.label}
              </Link>
            )
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <a
            href="tel:+390473238030"
            data-testid="header-phone"
            className="hidden xl:flex items-center gap-1.5 text-xs font-inter text-hb-muted hover:text-hb-dark transition-colors"
          >
            <Phone size={11} /> +39 0473 238 030
          </a>

          {/* Language Switcher */}
          <div className="hidden md:flex items-center gap-1 border border-hb-border rounded-sm overflow-hidden" data-testid="lang-switcher">
            <button
              onClick={() => setLang("de")}
              className={`text-[10px] font-semibold tracking-widest px-2.5 py-1.5 transition-all font-inter ${lang === "de" ? "bg-hb-dark text-white" : "text-hb-muted hover:text-hb-dark"}`}
              data-testid="lang-de"
              aria-label="Deutsch"
            >DE</button>
            <button
              onClick={() => setLang("it")}
              className={`text-[10px] font-semibold tracking-widest px-2.5 py-1.5 transition-all font-inter ${lang === "it" ? "bg-hb-dark text-white" : "text-hb-muted hover:text-hb-dark"}`}
              data-testid="lang-it"
              aria-label="Italiano"
            >IT</button>
          </div>

          <Link
            to="/anfrage"
            data-testid="header-cta-btn"
            className="hidden md:inline-flex text-xs tracking-widest uppercase font-semibold px-6 py-3 bg-hb-dark text-white hover:bg-hb-dark-secondary transition-all duration-200"
          >
            <T de="Anfrage stellen" it="Richiedi preventivo" />
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-hb-dark"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-hb-border px-6 py-6 space-y-1"
        >
          {items.map((item) => (
            <div key={item.label}>
              <Link
                to={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="block py-3 text-xs tracking-widest uppercase font-semibold text-hb-dark border-b border-hb-border/50"
              >
                {item.label}
              </Link>
              {item.dropdown && (
                <div className="pl-4">
                  {item.dropdown.map((sub) => (
                    <Link
                      key={sub.href}
                      to={sub.href}
                      className="block py-2 text-xs text-hb-text"
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link
            to="/anfrage"
            data-testid="mobile-cta-btn"
            className="block mt-4 btn-primary text-center text-xs"
          >
            <T de="Anfrage stellen" it="Richiedi preventivo" />
          </Link>
          {/* Mobile language switcher */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-hb-border/50">
            <Globe size={12} className="text-hb-muted" />
            <button onClick={() => setLang("de")} className={`text-xs font-semibold font-inter px-2 py-1 transition-colors ${lang === "de" ? "text-hb-dark" : "text-hb-muted"}`} data-testid="mobile-lang-de">Deutsch</button>
            <span className="text-hb-muted text-xs">|</span>
            <button onClick={() => setLang("it")} className={`text-xs font-semibold font-inter px-2 py-1 transition-colors ${lang === "it" ? "text-hb-dark" : "text-hb-muted"}`} data-testid="mobile-lang-it">Italiano</button>
          </div>
        </div>
      )}
    </header>
  );
}
